import { useFormik } from "formik";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../../components/buttons/button";
import { ContainerCard } from "../../../../../components/cards/containerCard";
import { InformationCard } from "../../../../../components/cards/infoCard";
import ComboBox from "../../../../../components/inputs/combobox";
import { DateInput } from "../../../../../components/inputs/dateInput";
import { PhoneInputSet } from "../../../../../components/inputs/phoneInputSet";
import { TextInput } from "../../../../../components/inputs/textInput";
import { BvnVerificationFlowModal } from "../../../../../components/modals/app_modals/bvn/bvnVerificationFlowModal";
import { ProfilePicture } from "../../../../../components/profile/profilePicture";
import { PageHeader } from "../../../../../components/typography/pageHeader";
import { useModalControl } from "../../../../../customHooks/useModalControl";
import {
  cleanHashes,
  parseDateStringToDate,
} from "../../../../../helperFunctions";
import {
  useLazyGetUserProfileQuery,
  useUpdateUserProfileMutation,
} from "../../../../../redux/features/auth/auth.api";
import {
  useGetCountriesQuery,
  useGetStatesQuery,
} from "../../../../../redux/features/settings/settings.api";
import { useAppSelector } from "../../../../../redux/hooks";
import { profileUpdateSchema } from "../../../../../validationSchema";

export const MemberProfileEdit = () => {
  const [uploadedPicture, setUploadedPicture] = useState<File | null>(null);
  const [searchValues, setSearchValues] = useState<{
    country: string;
    state: string;
  }>({
    country: "",
    state: "",
  });

  const { profile } = useAppSelector((state) => state.auth);

  const profilePicRef = useRef<HTMLInputElement | null>(null);

  const [updateProfile, { isLoading: updatingProfile }] =
    useUpdateUserProfileMutation();

  const [fetchProfile] = useLazyGetUserProfileQuery();

  const navigate = useNavigate();

  const modalControls = useModalControl();

  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    touched,
    errors,
    setFieldValue,
  } = useFormik({
    initialValues: {
      firstName: cleanHashes(profile?.FirstName),
      middleName: cleanHashes(profile?.MiddleName),
      lastName: cleanHashes(profile?.LastName),
      dateOfBirth: cleanHashes(profile?.DateOfBirth)
        ? parseDateStringToDate(
            cleanHashes(profile?.DateOfBirth),
            "YYYY-MM-DD",
            true
          )
        : undefined,
      phoneNumber: cleanHashes(
        `${profile?.PhoneNumberCode}${profile?.PhoneNumber}`
      )
        ? cleanHashes(`+${profile?.PhoneNumberCode}${profile?.PhoneNumber}`)
        : undefined,
      email: cleanHashes(profile?.Email),
      address: cleanHashes(profile?.Address),
      country: cleanHashes(profile?.Country),
      countrySec: cleanHashes(profile?.Country),
      state: cleanHashes(profile?.State),
      stateSec: cleanHashes(profile?.State),
    },
    enableReinitialize: true,
    validationSchema: profileUpdateSchema,
    onSubmit: async (data) => {
      const payload = {
        dob: data?.dateOfBirth
          ? new Date(data?.dateOfBirth).toISOString().split("T")[0]
          : null,
        phone: values.phoneNumber?.replaceAll(" ", ""),
        mName: data?.middleName,
        fName: data?.firstName,
        lName: data?.lastName,
        address: data?.address,
        country: data?.country,
        state: data?.state,
        // bvn: data?.bvn,
        pic: uploadedPicture,
      };

      //   Append values to form data
      const formData = new FormData();

      Object.keys(payload).forEach((key) => {
        const typedKey = key as keyof typeof payload;
        const value = payload[typedKey];

        if (value !== null && value !== undefined) {
          formData.append(typedKey, value);
        }
      });

      const resp = await updateProfile(formData);

      if (resp?.data?.success) {
        await fetchProfile();
        navigate("/app/settings");
      }
    },
  });

  const { data: countries, isFetching: fetchingCountries } =
    useGetCountriesQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  const { data: states, isFetching: fetchingStates } = useGetStatesQuery(
    {
      country: values.country ?? "",
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !values.country,
    }
  );

  return (
    <>
      <div className="w-full">
        <PageHeader
          hasBack={true}
          onBackClick={() => navigate("/app/settings?tab=profile")}
          pageTitle={"Edit Profile"}
          hasRightChildren
          rightChildren={
            <Button
              text="Save"
              onClick={() => {
                handleSubmit();
              }}
              type="button"
              className="!min-w-[82px] md:!min-w-[107px] !w-fit"
              size="medium"
              loading={updatingProfile}
              disabled={updatingProfile}
              variation="primary"
            />
          }
          className="max-md:hidden"
        />

        <div className="md:mt-[28px] flex flex-row flex-wrap width-lim:flex-nowrap gap-2 md:gap-6">
          <ContainerCard className="!px-4 md:!px-[24px] w-full width-lim:max-w-[655px] order-last width-lim:order-first">
            <p className="text-pry-dark-green-400 font-bold text-base mb-[32px]">
              Personal Information
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px] w-full">
              <TextInput
                type="text"
                name="firstName"
                value={values?.firstName}
                placeholder="Your first name"
                onChange={handleChange}
                onBlur={handleBlur}
                hasIcon={false}
                iconSvg={undefined}
                errors={
                  touched?.firstName && errors?.firstName
                    ? errors?.firstName
                    : ""
                }
                inputClass=""
                className="disabled-input"
                containerClassName=""
                hasTitle
                title="First Name"
                readOnly
              />

              <TextInput
                type="text"
                name="middleName"
                value={values?.middleName}
                placeholder="Your middle name (Optional)"
                onChange={handleChange}
                onBlur={handleBlur}
                hasIcon={false}
                iconSvg={undefined}
                errors={
                  errors?.middleName && touched?.middleName
                    ? errors?.middleName
                    : ""
                }
                inputClass=""
                className="disabled-input"
                containerClassName=""
                hasTitle
                title="Middle Name"
                readOnly
              />

              <TextInput
                type="text"
                name="lastName"
                value={profile?.LastName}
                placeholder="Your last name"
                onChange={handleChange}
                onBlur={handleBlur}
                hasIcon={false}
                iconSvg={undefined}
                errors={
                  errors?.lastName && touched?.lastName ? errors?.lastName : ""
                }
                inputClass=""
                className="disabled-input"
                containerClassName=""
                hasTitle
                title="Last Name"
                readOnly
              />

              <DateInput
                name="dateOfBirth"
                date={values?.dateOfBirth}
                placeholder="Date of Birth"
                setDate={(val) => {
                  setFieldValue("dateOfBirth", val?.toDate());
                }}
                onBlur={handleBlur}
                errors={
                  errors?.dateOfBirth && touched?.dateOfBirth
                    ? errors?.dateOfBirth
                    : ""
                }
                title="Date of Birth"
                disableFuture
                inputClassName="disabled-input"
                disabled
              />

              <PhoneInputSet
                onChange={(val) => {
                  setFieldValue("phoneNumber", val);
                }}
                value={values.phoneNumber ?? ""}
                errors={
                  errors?.phoneNumber && touched?.phoneNumber
                    ? errors?.phoneNumber
                    : ""
                }
                onBlur={handleBlur}
                title="Phone Number"
                name="phoneNumber"
                className="col-span-full"
                inputClassName="disabled-input"
                dropdownClassName="disabled-input"
                disabled={true}
              />

              <TextInput
                type="text"
                name="email"
                value={values?.email}
                placeholder="Your email address"
                onChange={handleChange}
                onBlur={handleBlur}
                hasIcon={false}
                iconSvg={undefined}
                errors={errors?.email && touched?.email ? errors?.email : ""}
                inputClass=""
                className="disabled-input"
                containerClassName="col-span-full"
                hasTitle
                title="Email Address"
                readOnly
              />

              <p className="text-pry-dark-green-400 font-bold text-base my-4 col-span-full border-t pt-8">
                Address
              </p>

              <TextInput
                type="text"
                name="address"
                value={values?.address || ""}
                placeholder="Your residential address"
                onChange={handleChange}
                onBlur={handleBlur}
                hasIcon={false}
                iconSvg={undefined}
                errors={
                  errors?.address && touched?.address ? errors?.address : ""
                }
                inputClass=""
                className=""
                containerClassName="col-span-full"
                hasTitle
                title="Home Address"
              />

              <ComboBox
                type="text"
                isDropdown
                name="countrySec"
                value={values?.countrySec}
                menuItems={countries?.data
                  ?.filter((c) => {
                    const s = searchValues.country.toLowerCase();

                    return c.Country.toLowerCase().includes(s);
                  })
                  ?.map((c) => ({
                    name: c.Country,
                    value: c.Country,
                  }))}
                placeholder="Country"
                onChange={(e) => {
                  handleChange(e);
                  setFieldValue("country", undefined);
                }}
                onMenuClose={() => {
                  setSearchValues((prev) => ({
                    ...prev,
                    country: "",
                  }));
                }}
                onBlur={handleBlur}
                hasIcon={false}
                errors={
                  errors?.country && touched.countrySec ? errors?.country : ""
                }
                menuClassName="!max-h-[250px] !min-w-[200px]"
                position="bottom start"
                onSearch={(searchValue) => {
                  setSearchValues((prev) => ({
                    ...prev,
                    country: searchValue,
                  }));
                }}
                onSelectOption={(val: string) => {
                  setSearchValues((prev) => ({ ...prev, country: "" }));
                  setFieldValue("country", val);
                  setFieldValue("countrySec", val);
                }}
                className=""
                hasTitle
                title="Country"
                containerClassName="!w-full"
                immediate
                loading={fetchingCountries}
              />

              <ComboBox
                type="text"
                isDropdown
                name="stateSec"
                value={values?.stateSec}
                menuItems={states?.data
                  ?.filter((c) => {
                    const s = searchValues.state.toLowerCase();

                    return c.name.toLowerCase().includes(s);
                  })
                  ?.map((c) => ({
                    name: c.name,
                    value: c.name,
                  }))}
                placeholder="State"
                onChange={(e) => {
                  handleChange(e);
                  setFieldValue("state", undefined);
                }}
                onMenuClose={() => {
                  setSearchValues((prev) => ({
                    ...prev,
                    state: "",
                  }));
                }}
                onBlur={handleBlur}
                hasIcon={false}
                errors={errors?.state && touched.stateSec ? errors?.state : ""}
                menuClassName="!max-h-[250px] !min-w-[200px]"
                position="bottom start"
                onSearch={(searchValue) => {
                  setSearchValues((prev) => ({
                    ...prev,
                    state: searchValue,
                  }));
                }}
                onSelectOption={(val: string) => {
                  setSearchValues((prev) => ({ ...prev, state: "" }));
                  setFieldValue("state", val);
                  setFieldValue("stateSec", val);
                }}
                className=""
                hasTitle
                title="State"
                containerClassName="!w-full"
                immediate
                loading={fetchingStates}
              />

              <Button
                text="Save"
                onClick={() => {
                  handleSubmit();
                }}
                type="button"
                className="md:hidden"
                loading={updatingProfile}
                disabled={updatingProfile}
                variation="primary"
              />
            </div>
          </ContainerCard>

          <div className="flex-1 width-lim:sticky-top">
            <InformationCard
              info={
                <div>
                  To change your{" "}
                  <span className="text-pry-green-200 font-medium">
                    email address
                  </span>
                  , please reach out to support.
                </div>
              }
            />

            <ContainerCard
              className="flex flex-col 
              items-center justify-center
              mt-4
              gap-6
              "
            >
              <ProfilePicture
                className="
              !w-[106px] !h-[106px] 
              !text-2xl !font-bold
              hover:!scale-100
              "
                tempSrc={
                  uploadedPicture
                    ? URL.createObjectURL(uploadedPicture)
                    : undefined
                }
              />

              <input
                type="file"
                accept="image/*"
                className="hidden"
                ref={profilePicRef}
                multiple={false}
                onChange={(e) => {
                  if (e?.target?.files && e?.target?.files[0]) {
                    setUploadedPicture(e?.target?.files[0]);
                  }
                }}
              />

              <div
                className="
              w-full 
              items-center justify-center 
              flex flex-row flex-wrap gap-2"
              >
                {uploadedPicture && (
                  <Button
                    text="Change"
                    onClick={() => {
                      profilePicRef?.current?.click();
                    }}
                    type="button"
                    className="!min-w-[80px] !max-w-fit"
                    size="small"
                    loading={false}
                    disabled={false}
                    variation="secondary"
                  />
                )}

                {!uploadedPicture && (
                  <Button
                    text={profile?.Picture ? "Change photo" : "Add photo"}
                    onClick={() => {
                      profilePicRef?.current?.click();
                    }}
                    type="button"
                    className="!min-w-[80px] !max-w-fit"
                    size="small"
                    loading={false}
                    disabled={false}
                    variation={false ? "primary" : "secondary-light"}
                  />
                )}
              </div>

              <div
                className="
              w-full max-w-[353px]
              text-center
              "
              >
                <div
                  className="
                font-bold text-base
                "
                >
                  Help cooperatives to identify you easily
                </div>

                <div className="mt-1 text-pry-dark-green-300/60">
                  Your picture will be visible to your cooperatives on
                  ThriftCorp
                </div>
              </div>
            </ContainerCard>
          </div>
        </div>
      </div>

      <BvnVerificationFlowModal
        modalControls={modalControls}
        onSuccess={() => {}}
      />
    </>
  );
};
